/**
 * PageNumberLabel — 한 낱장의 문서 페이지번호 표시 조각(정본).
 *
 * 결정: `docs/decisions/2026-09-30-composer-document-page-numbers.md`
 *
 * **왜 rc에 있고 왜 호출부가 한 줄인가**
 * 낱장 래퍼가 둘이다 — rc `PagedComposerView`(상세·인쇄 미리보기·PDF·공유·외부 서명·admin 시뮬레이션)와
 * admin `PagedEditBody`(편집 캔버스·팝오버 미리보기·미리보기 팝아웃). 두 벌로 만들면 갈리고, 갈린 날
 * **캔버스가 출력과 다른 번호를 보여 준다** — 이 기능이 막으려는 바로 그 사고다. 그래서 위치·형식·
 * 스타일을 이 조각이 전부 소유하고, 두 래퍼의 호출부는 **글자 그대로 같은 한 줄**이다:
 *
 * ```tsx
 * <PageNumberLabel page={template.page} pageIndex={i} pageCount={pagePlan.length} />
 * ```
 *
 * 배열 인덱싱(`resolvePageNumbers(...)[pageIndex]`)까지 이 안에 두어 호스트가 조립에 개입할 여지를
 * 없앴다. 낱장 수만큼 배열을 다시 만들지만 낱장 수는 작다(수십) — 계약의 단일성이 그 비용보다 크다.
 *
 * **왜 CSS `@page`/`counter(page)`가 아닌가**
 * 이 앱의 PDF는 `page.pdf()`가 아니라 **낱장 스크린샷**이다(`pdf.processor.ts` `pageElement.screenshot()`
 * → `embedPng`). `@page`·마진박스·`counter(page)`는 `page.pdf()` 경로의 기능이라 이 파이프라인에서
 * 아무 효과가 없다. DOM에 그리는 것이 유일한 길이고, 그것이 「캔버스 = 출력」을 **구조로** 보장한다.
 *
 * ⚠️⚠️ **그 보장의 한계 — 「같은 조각」은 「같은 입력」이 아니다.**
 * 두 래퍼가 이 조각을 공유하므로 *같은 설정이면* 픽셀로 같은 번호가 나온다. 그런데 **설정 자체가
 * 다른 세대일 수 있다**:
 *   · admin 편집 캔버스는 `template.page` — **현행 템플릿**을 직접 본다.
 *   · rc 래퍼(상세·인쇄·PDF·공유·외부 서명)는 그 문서의 `report.templateSnapshot.page` —
 *     **생성 시점 박제**를 본다(`reports/utils/template-snapshot.util.ts`가 `page`를 통째 복사한다).
 *
 * 그래서 옵션을 켜면 **캔버스는 즉시** 번호가 보이지만 **이미 만들어진 문서의 인쇄·PDF는 안 바뀐다**
 * — 새로 만든 문서에서만 보인다. 즉 「캔버스 ↔ 출력 일치」는 **같은 템플릿 세대일 때만** 참이고,
 * **기존 문서와의 불일치는 결함이 아니라 정상**이다(그 박제가 곧 기본값 꺼짐의 하위호환 근거이기도
 * 하다 — 결재 완료본은 법적 증빙이라 이미 배포된 문서의 외형이 조용히 바뀌면 사고다).
 *
 * ⇒ 실측하는 사람에게: **옵션을 켠 뒤 «새» 문서로** 재라. 켠 직후 기존 문서를 열어 「안 나온다」를
 * 결함으로 읽지 말 것. 그 차이 자체가 관측 포인트다.
 *
 * **레이아웃 중립 — 측정을 흔들지 않는다**
 * `position:absolute`(흐름 밖) + `pointerEvents:'none'`(편집 방해 없음)이고, `data-composer-*` 속성을
 * 쓰지 않는다. rc `measureBody`는 전부 `data-composer-*` 셀렉터로만 조회하므로 이 요소는 측정에
 * **비가시**다 — 그래서 `pagePlan`(낱장 배치)에 영향을 주지 않는다. 표시 마커는 `data-page-number`다.
 *
 * ⚠️ 번호가 없는 낱장(출력 시작 이전)에는 **요소를 아예 그리지 않는다**(`null` 반환). 늘 그리고
 * 내용만 비우면 요소의 **존재가 거짓을 말하고**, DOM 검사·브라우저 실측이 「요소가 있다 = 번호가
 * 찍힌다」로 오독한다.
 */
import type { CSSProperties } from 'react';
import type { PageSpec } from '@safetyhub/shared';
import { resolvePageNumbers, resolvePageNumberPosition } from '@safetyhub/shared';

/** 표시 마커 속성 — spec·가드가 문자열을 손으로 적지 않도록 여기서 파생한다. */
export const PAGE_NUMBER_ATTR = 'data-page-number';

/** 글자 크기(px, 794 원본 좌표). 본문 기본보다 작게 — 문서 footer 관례. */
export const PAGE_NUMBER_FONT_SIZE_PX = 12;

/** 글자색 — 본문과 같은 계열의 near-black(결재 증빙 문서라 회색 크롬이 아니다). */
export const PAGE_NUMBER_COLOR = '#111827';

/**
 * 최소 표시 높이(px) — 하단 여백이 이보다 작아도 번호가 보이게 한다.
 *
 * 여백 0인 템플릿에서 높이를 여백에 맞추면 **켰는데 안 보인다**가 된다. 본문과 겹치더라도 보이는
 * 쪽을 택한다 — 저작자가 「켰다」고 믿는 채 아무것도 안 나오는 것이 더 나쁘고, 겹침은 화면에서
 * 즉시 보여 저작자가 여백을 고칠 수 있다(활성 6종은 하단 여백 57px이라 무영향).
 */
export const PAGE_NUMBER_MIN_HEIGHT_PX = 18;

/**
 * 번호 띠의 실제 높이(px) — 하단 여백, 단 최소 표시 높이 아래로는 안 내려간다.
 *
 * 아래 컴포넌트가 **이 함수로** 높이를 구한다(값의 파생을 한 곳에 둔다). 저작 화면이 「번호가 본문을
 * 침범하나」를 물을 때도 같은 값을 봐야 하므로 공개한다 — 상수를 넘겨받아 호출측이 `Math.max`를
 * 다시 쓰면 그 순간 규칙이 두 곳이 된다.
 */
export function pageNumberBandHeightPx(page: PageSpec): number {
  return Math.max(page.margins.bottom, PAGE_NUMBER_MIN_HEIGHT_PX);
}

/**
 * 번호 띠가 **본문 영역을 침범하는가** — 하단 여백이 최소 표시 높이보다 작을 때 참.
 *
 * 왜 필요한가: 여백이 0이어도 번호를 보이게 하는 것이 옳지만(위 `PAGE_NUMBER_MIN_HEIGHT_PX`),
 * 그 결과인 **겹침을 화면이 말하지 않으면** 저작자는 겹친 화면을 보고 「옵션이 고장났다」고 읽는다.
 * 엔진이 조용히 겹치는 것을 숨기지 않는 것이 「아무 낱장에도 안 찍힌다」 경고와 **같은 계열**이다.
 *
 * 꺼진 스펙은 `false`다 — 그리지 않는 것은 겹치지도 않는다(`pageNumbersNeverShow`와 같은 대칭:
 * 「꺼 두었다」는 경고할 상태가 아니다).
 *
 * ⚠️ **이 술어가 rc에 있고 `pageNumbersNeverShow`가 shared에 있는 것은 실수가 아니다 — 묻는 것이
 * 다르다.** 번호 «문자열»(무엇을 찍나·어느 낱장에 찍나)은 **데이터**라 shared가 소유하고, 「띠가
 * 본문을 침범하나」는 **픽셀**(최소 표시 높이·여백)이라 rc가 소유한다. shared는 스타일을 모른다.
 * 둘을 한 곳으로 「통일」하면 shared가 렌더 상수를 들거나 rc가 문자열 규약을 들게 되고, 그것이
 * 이 파일 상단이 경계하는 드리프트의 입구다.
 */
export function pageNumberOverlapsBody(page: PageSpec): boolean {
  if (!page.pageNumber?.enabled) return false;
  return page.margins.bottom < PAGE_NUMBER_MIN_HEIGHT_PX;
}

export interface PageNumberLabelProps {
  /** 템플릿 전역 용지 사양 — 번호 설정(`page.pageNumber`)과 여백을 함께 읽는다. */
  page: PageSpec;
  /** 이 낱장의 0-base 인덱스. */
  pageIndex: number;
  /** 문서 전체 낱장 수(`pagePlan.length`). `N / M`의 `M` 산출 입력. */
  pageCount: number;
}

export function PageNumberLabel({ page, pageIndex, pageCount }: PageNumberLabelProps) {
  // 문자열 조립·표시 여부는 shared `resolvePageNumbers`가 단독 소유한다(여기서 조건을 다시 쓰면
  // 규칙이 늘어난 날 조용히 갈린다).
  const label = resolvePageNumbers(pageCount, page.pageNumber)[pageIndex] ?? null;
  if (label === null) return null;

  const position = resolvePageNumberPosition(page.pageNumber);
  const style: CSSProperties = {
    position: 'absolute',
    bottom: 0,
    // 좌우를 여백에 맞춘다 — 중앙 정렬은 **본문 영역**의 중앙이다(Word와 같은 기준. 좌우 여백이
    // 같으면 용지 중앙과 동일하고, 다르면 본문과 정렬된 쪽이 맞다). 우측은 본문 오른쪽 끝에 붙는다.
    left: page.margins.left,
    right: page.margins.right,
    // 높이 파생은 `pageNumberBandHeightPx` 한 곳 — 저작 화면의 겹침 경고가 같은 값을 본다.
    // ⚠️ 이 한 줄을 `Math.max(...)`로 바꾸면 **값은 같아서 rc spec이 통과한다**(2026-09-30 프로브 실측).
    // 그 축의 탐지기는 admin `pagedEditBodyPageNumber.test.tsx`의 소스 계약 케이스
    // `[되돌림] 조각의 높이 파생이 한 곳이다`다 — 거기 이유가 적혀 있다.
    height: pageNumberBandHeightPx(page),
    display: 'flex',
    alignItems: 'center',
    justifyContent: position === 'bottom-right' ? 'flex-end' : 'center',
    // 흐름 밖 + 히트 없음 — 편집 캔버스의 노드 선택·드래그를 방해하지 않는다.
    pointerEvents: 'none',
    // 본문 기본 폰트와 같게(RenderTemplate pageStyle). 이 요소는 RenderTemplate 밖(낱장 래퍼의
    // 직접 자식)이라 상속으로는 안 온다 — 명시해야 인쇄·PDF에서 본문과 같은 서체로 나온다.
    fontFamily: "'Noto Serif KR', serif",
    fontSize: PAGE_NUMBER_FONT_SIZE_PX,
    lineHeight: 1,
    color: PAGE_NUMBER_COLOR,
    whiteSpace: 'nowrap',
  };

  // 마커는 리터럴로 쓴다(JSX 스프레드보다 타입이 확실하다). `PAGE_NUMBER_ATTR`과 어긋나면
  // 그 상수로 조회하는 spec 둘(rc `pageNumberLabel.test.tsx` / admin `pagedEditBodyPageNumber.test.tsx`)이
  // 요소를 못 찾아 red가 된다 — 그 둘이 이 한 쌍의 탐지기다.
  return (
    <div data-page-number={label} style={style}>
      {label}
    </div>
  );
}

export default PageNumberLabel;
